import { useMemo } from 'react';

import { useCartContext } from 'features/cart/hooks/use-cart-context';
import { ICartEntry } from 'features/cart/types';
import { isEmpty } from 'lodash-es';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { experimentalMessages } from 'pages/cart/cart-item/cart-item-offer';
import { useFeedbackAdditionalInfo } from 'pages/cart/cart-item/hooks/use-feedback-additional-info';
import { useLoyaltyWalmart } from 'pages/loyalty/loyalty-partners/walmart/use-loyalty-walmart';
import { selectors, useAppSelector } from 'state/global-state';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import {
  getWalmartSeasonalConfigOfferIds,
  isAnyWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { LoyaltyOffer } from 'state/loyalty/types';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';

export const useCartOfferValidation = (cartEntry?: ICartEntry) => {
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const cmsOffersMap = getCmsOffersMapByCmsId(cmsOffers);
  const selectedOffer =
    (cartEntry && cmsOffersMap[cartEntry.id]) ??
    (cartEntry
      ? ({ _id: cartEntry.id, loyaltyEngineId: cartEntry.referenceId } as LoyaltyOffer)
      : undefined);
  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const loyaltyWalmartUILoading = useAppSelector(selectors.loyalty.selectLoyaltyWalmartUILoading);
  const { data: loyaltyWalmart } = useLoyaltyWalmart();
  const { loading: loadingFeedbackAdditionalInfo, additionalInfo } = useFeedbackAdditionalInfo({
    loyaltyOfferId: selectedOffer?.loyaltyEngineId || '',
  });

  const isWalmartOfferSelected =
    selectedOffer?._id &&
    (isAnyWalmartOffer(selectedOffer) ||
      getWalmartSeasonalConfigOfferIds(loyaltyWalmart).includes(selectedOffer._id));

  const offerFeedback = selectedOffer
    ? offersFeedbackMap[selectedOffer.loyaltyEngineId ?? '']
    : undefined;

  const isOfferApplied = isEmpty(offerFeedback);
  const isLoading = isLoadingOffers || loadingFeedbackAdditionalInfo;

  const { cartEntries } = useCartContext();

  const feedbackMessages = useMemo(
    () =>
      offerFeedback?.map(feedback =>
        getEvaluationResultMessage({
          evaluationResult: feedback,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries: cartEntries.map(entry => ({
            _id: entry.id,
            name: entry.name,
          })) as ILegacyCartEntry[],
          experimentalMessages,
          forceOnlyStringMessage: true,
          additionalInfo,
        })
      ),
    [offerFeedback, cartEntries, additionalInfo]
  );

  return useMemo(
    () => ({
      isLoading,
      isOfferApplied,
      feedbackMessages,
      walmart: {
        isOfferSelected: isWalmartOfferSelected,
        isLoading: loyaltyWalmartUILoading,
      },
    }),
    [isLoading, isOfferApplied, feedbackMessages, isWalmartOfferSelected, loyaltyWalmartUILoading]
  );
};
